import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  TtoDateRange,
  TtoEngagementDto,
  TtoGranularity,
  TtoReportingDto,
  TtoSummaryDto,
  TtoTasksDto,
  TtoTimeseriesDto,
} from '../../common/general-types/tiktok-one-reporting.dto';

@Injectable({
  providedIn: 'root',
})
export class TiktokOneReportingService {
  private http = inject(HttpClient);

  private get headers() {
    return { 'Content-Type': 'application/json' };
  }

  private base(briefId: string): string {
    return `${environment.shout_api.url}/tiktok-one/tcm/briefs/${briefId}`;
  }

  getSummary(briefId: string): Observable<{ data: TtoSummaryDto }> {
    return this.http.get<{ data: TtoSummaryDto }>(`${this.base(briefId)}/summary`, {
      headers: this.headers,
    });
  }

  getTasks(briefId: string): Observable<{ data: TtoTasksDto }> {
    return this.http.get<{ data: TtoTasksDto }>(`${this.base(briefId)}/tasks`, {
      headers: this.headers,
    });
  }

  getReporting(
    briefId: string,
    params: TtoDateRange & { localeOrRegion?: string },
  ): Observable<{ data: TtoReportingDto }> {
    const { startDate, endDate, localeOrRegion } = params;
    const queryParams: Record<string, string> = { startDate, endDate };
    if (localeOrRegion) queryParams['localeOrRegion'] = localeOrRegion;
    return this.http.get<{ data: TtoReportingDto }>(`${this.base(briefId)}/reporting`, {
      headers: this.headers,
      params: queryParams,
    });
  }

  getEngagement(
    briefId: string,
    params: TtoDateRange & { localeOrRegion?: string },
  ): Observable<{ data: TtoEngagementDto }> {
    const { startDate, endDate, localeOrRegion } = params;
    const queryParams: Record<string, string> = { startDate, endDate };
    if (localeOrRegion) queryParams['localeOrRegion'] = localeOrRegion;
    return this.http.get<{ data: TtoEngagementDto }>(`${this.base(briefId)}/engagement`, {
      headers: this.headers,
      params: queryParams,
    });
  }

  getTimeseries(
    briefId: string,
    params: TtoDateRange & { metric: string; granularity: TtoGranularity },
  ): Observable<{ data: TtoTimeseriesDto }> {
    const { startDate, endDate, metric, granularity } = params;
    return this.http.get<{ data: TtoTimeseriesDto }>(`${this.base(briefId)}/timeseries`, {
      headers: this.headers,
      params: { startDate, endDate, metric, granularity },
    });
  }

  getIndividualPostResults(ttoCampaignId: string) {
    const get_tto_indiviaul_url = `${environment.shout_api.url}/tiktok-one/individual-post-results`;
    return this.http.post<any>(get_tto_indiviaul_url, { ttoCampaignId: ttoCampaignId, countryCode: 'GB' }, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  getTtoEngagementsDashboard(ttoCampaignId: string) {
    const get_tto_results_dashboard_url = `${environment.shout_api.url}/tiktok-one/tto-results-dashboard/${ttoCampaignId}`;
    return this.http.get<any>(get_tto_results_dashboard_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  getTtoCampaign() {
    const get_tto_campaign_url = `${environment.shout_api.url}/tiktok-one/tto-campaign`;
    return this.http.get<any>(get_tto_campaign_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  getSparkAuthStatus(videoId: string) {
    const get_spark_auth_status_url = `${environment.shout_api.url}/tiktok-one/spark-auth-status/${videoId}`;
    return this.http.get<any>(get_spark_auth_status_url, {
      headers: this.headers,
    });
  }

  requestSparkAuth(videoId: string) {
    const apply_for_auth_url = `${environment.shout_api.url}/tiktok-one/request-spark-auth`;
    return this.http.post<any>(apply_for_auth_url, { video_id: videoId }, {
      headers: this.headers,
    });
  }

  applySparkAuthorizationCode(videoId: string, authorizationCode: string) {
    const apply_spark_authorization_code_url = `${environment.shout_api.url}/tiktok-one/apply-spark-authorization-code`;
    return this.http.post<any>(apply_spark_authorization_code_url, { spark_code: authorizationCode }, {
      headers: this.headers,
    });
  }
}
